
import { supabase } from "@/integrations/supabase/client";
import { Settings, defaultSettings } from "@/types/settings";

// Clé pour le stockage dans localStorage
const SETTINGS_CACHE_KEY = 'app_settings_cache';

/**
 * Récupère les paramètres depuis la base de données
 */
export async function fetchSettingsFromDB(): Promise<Settings | null> {
  try {
    const { data, error } = await supabase
      .from("settings")
      .select()
      .eq('id', 1)
      .single();

    if (error) {
      console.error("Erreur lors de la récupération des paramètres:", error);
      return null;
    }

    // Conversion des noms de colonnes de la base de données vers les noms utilisés en frontend
    return {
      title: data.title || defaultSettings.title,
      email: data.email || defaultSettings.email,
      phone: data.phone || defaultSettings.phone,
      whatsappLink: data.whatsapp_link || defaultSettings.whatsappLink,
      
      footerText: data.footer_text || defaultSettings.footerText,
      logoUrl: data.logo_url || defaultSettings.logoUrl,
      showVirtualAssistant: data.show_virtual_assistant ?? defaultSettings.showVirtualAssistant,
      
      // Banner settings
      bannerEnabled: data.banner_enabled ?? defaultSettings.bannerEnabled,
      bannerText: data.banner_text || defaultSettings.bannerText,
      bannerBgColor: data.banner_bg_color || defaultSettings.bannerBgColor,
      bannerTextColor: data.banner_text_color || defaultSettings.bannerTextColor,
      bannerLink: data.banner_link || defaultSettings.bannerLink,
      bannerLinkText: data.banner_link_text || defaultSettings.bannerLinkText,
      
      // Nouvelles dates de mise à jour des pages légales
      cgvUpdatedAt: data.cgv_updated_at || defaultSettings.cgvUpdatedAt,
      mentionsLegalesUpdatedAt: data.mentions_legales_updated_at || defaultSettings.mentionsLegalesUpdatedAt,
      cguUpdatedAt: data.cgu_updated_at || defaultSettings.cguUpdatedAt,
      privacyPolicyUpdatedAt: data.privacy_policy_updated_at || defaultSettings.privacyPolicyUpdatedAt,
    };
  } catch (error) {
    console.error("Erreur lors de la récupération des paramètres:", error);
    return null;
  }
}

/**
 * Enregistre les paramètres dans le localStorage
 */
export function saveSettingsToCache(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error("Erreur lors de l'enregistrement des paramètres dans le cache:", error);
  }
}

/**
 * Charge les paramètres depuis le localStorage
 */
export function loadSettingsFromCache(): Settings {
  try {
    const cachedSettings = localStorage.getItem(SETTINGS_CACHE_KEY);
    return cachedSettings ? JSON.parse(cachedSettings) : defaultSettings;
  } catch (error) {
    console.error("Erreur lors du chargement des paramètres depuis le cache:", error);
    return defaultSettings;
  }
}

/**
 * Émet un évènement pour notifier les composants d'un changement de paramètres
 */
export function broadcastSettingsChange(source: string, data: Settings): void {
  window.dispatchEvent(new CustomEvent('settings-changed', { 
    detail: { source, data, timestamp: Date.now() } 
  }));
}
